import { Box, Modal, Stack, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Command } from 'cmdk';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { visibleNavItems } from '@/constants/navItems';
import { useAuth } from '@/contexts/AuthContext';
import { useHotkey } from '@/hooks/useHotkey';

export interface CommandPaletteProps {
  /** External open state — used by cmd+K hotkey owner. Optional; if not
   * provided, the palette manages its own. */
  open?: boolean;
  onClose?: () => void;
}

export function CommandPalette(props: CommandPaletteProps = {}): ReactElement {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = props.open ?? internalOpen;
  const close = props.onClose ?? (() => setInternalOpen(false));
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const navCommands = visibleNavItems(user?.email).map((item) => ({
    label: item.label === 'past' ? 'past investigations' : item.label,
    path: item.to,
  }));

  // own-managed cmd+k hotkey if no external control
  useHotkey('cmd+k', () => {
    if (props.open === undefined) setInternalOpen((v) => !v);
  });
  useHotkey('ctrl+k', () => {
    if (props.open === undefined) setInternalOpen((v) => !v);
  });

  const [search, setSearch] = useState('');
  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  function go(path: string): void {
    navigate(path);
    close();
  }

  function runHidden(name: string): void {
    window.dispatchEvent(new CustomEvent('sherlock:hidden-command', { detail: { name } }));
    close();
  }

  return (
    <Modal
      open={open}
      onClose={close}
      sx={{ zIndex: 1300 }}
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(10, 10, 13, 0.7)' } },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          ...(isMobile
            ? { inset: 0 }
            : { top: '15%', left: '50%', transform: 'translateX(-50%)', width: 520 }),
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: isMobile ? 0 : 1,
          boxShadow: '0 16px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(217,119,87,0.06)',
          overflow: 'hidden',
        }}
      >
        <Command
          shouldFilter
          loop
          style={{
            outline: 'none',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: isMobile ? '100vh' : '60vh',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              p: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
              backgroundColor: 'background.deeper',
            }}
          >
            <Box sx={{ color: 'primary.main', fontFamily: 'monospace', fontWeight: 700 }}>
              ›
            </Box>
            <Command.Input
              autoFocus
              value={search}
              onValueChange={setSearch}
              placeholder="Type a command or search… (try /help)"
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: '#E8DDC9',
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: 14,
              }}
            />
            <Box sx={{ color: 'text.faintest', fontSize: 10, fontFamily: 'monospace' }}>
              esc
            </Box>
          </Stack>

          <Command.List style={{ maxHeight: 'calc(60vh - 60px)', overflow: 'auto' }}>
            <Command.Empty>
              <Box
                sx={{
                  p: 3,
                  color: 'text.faintest',
                  fontFamily: 'monospace',
                  fontSize: 12,
                }}
              >
                no matches — try /help
              </Box>
            </Command.Empty>

            <Command.Group heading="navigate">
              {navCommands.map((cmd) => (
                <Command.Item
                  key={cmd.path}
                  value={cmd.label}
                  onSelect={() => go(cmd.path)}
                >
                  <Stack direction="row" alignItems="center" sx={{ p: 1.25, gap: 1 }}>
                    <Box sx={{ color: 'primary.main', minWidth: 14 }}>›</Box>
                    <Box sx={{ flex: 1, color: 'text.primary' }}>{cmd.label}</Box>
                  </Stack>
                </Command.Item>
              ))}
            </Command.Group>

            <Command.Group heading="hidden commands">
              <Command.Item value="/help" onSelect={() => runHidden('help')}>
                <Box sx={{ p: 1.25, color: 'text.primary', fontFamily: 'monospace' }}>
                  /help{' '}
                  <Box component="span" sx={{ color: 'text.faintest' }}>
                    — list hidden commands
                  </Box>
                </Box>
              </Command.Item>
              <Command.Item value="/elementary" onSelect={() => runHidden('elementary')}>
                <Box sx={{ p: 1.25, color: 'text.primary', fontFamily: 'monospace' }}>
                  /elementary{' '}
                  <Box component="span" sx={{ color: 'text.faintest' }}>
                    — Elementary, my dear Watson
                  </Box>
                </Box>
              </Command.Item>
              <Command.Item value="/sudo" onSelect={() => runHidden('sudo')}>
                <Box sx={{ p: 1.25, color: 'text.primary', fontFamily: 'monospace' }}>
                  /sudo{' '}
                  <Box component="span" sx={{ color: 'text.faintest' }}>
                    — nice try
                  </Box>
                </Box>
              </Command.Item>
              <Command.Item value="/coffee" onSelect={() => runHidden('coffee')}>
                <Box sx={{ p: 1.25, color: 'text.primary', fontFamily: 'monospace' }}>
                  /coffee
                </Box>
              </Command.Item>
              <Command.Item value="/matrix" onSelect={() => runHidden('matrix')}>
                <Box sx={{ p: 1.25, color: 'text.primary', fontFamily: 'monospace' }}>
                  /matrix
                </Box>
              </Command.Item>
              <Command.Item value="/whoami" onSelect={() => runHidden('whoami')}>
                <Box sx={{ p: 1.25, color: 'text.primary', fontFamily: 'monospace' }}>
                  /whoami
                </Box>
              </Command.Item>
              <Command.Item value="/mute" onSelect={() => runHidden('mute')}>
                <Box sx={{ p: 1.25, color: 'text.primary', fontFamily: 'monospace' }}>
                  /mute{' '}
                  <Box component="span" sx={{ color: 'text.faintest' }}>
                    — silence the buddy
                  </Box>
                </Box>
              </Command.Item>
              <Command.Item value="/unmute" onSelect={() => runHidden('unmute')}>
                <Box sx={{ p: 1.25, color: 'text.primary', fontFamily: 'monospace' }}>
                  /unmute{' '}
                  <Box component="span" sx={{ color: 'text.faintest' }}>
                    — bring the buddy back
                  </Box>
                </Box>
              </Command.Item>
            </Command.Group>
          </Command.List>
        </Command>
      </Box>
    </Modal>
  );
}
